import { type ElementType, type ComponentProps } from 'react'
import {
  type PromoBannerType,
  type AstroBannerData,
  type FeedbackBannerType,
} from '@/features/cms/components/CMS/types'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { FeedbackBanner, PromoAssetBanner, PromoBanner } from '@shipt/design-system-banner'
import { renderElement } from '@/features/cms/components/CMS/RichTextRenderer'
import {
  InfoCircleFillIcon,
  NegativeCircleFillIcon,
  PositiveCircleFillIcon,
  WarningTriangleFillIcon,
  FavoriteIcon,
  BuildingStoreIcon,
  ChevronRightIcon,
  DealIcon,
} from '@shipt/design-system-icons'
import { Image } from '@/features/shared/elements/Image/NextImage'
import styled from 'styled-components'
import { Box, Stack } from '@shipt/design-system-layouts'
import { TextButton } from '@shipt/design-system-buttons'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { Headline } from '@shipt/design-system-typography'
import { routes } from '@shared/constants/routes'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { getTargetCircleQueryParams } from '@/features/shared/components/Headers/Circle360Header/utils'
import { useSearchParams } from 'next/navigation'

type FeedbackBannerPropsType = ComponentProps<typeof FeedbackBanner>
function mapVariantToVariant(
  variant?: FeedbackBannerType['variant'],
): FeedbackBannerPropsType['variant'] {
  if (!variant) return 'info'
  switch (variant) {
    case 'Info':
      return 'info'
    case 'Positive':
      return 'positive'
    case 'Negative':
      return 'negative'
    case 'Warning':
      return 'warning'
    default:
      return 'info'
  }
}
function mapVariantToIcon(variant?: FeedbackBannerType['variant']): ElementType | undefined {
  if (!variant) return undefined
  switch (variant) {
    case 'Info':
      return InfoCircleFillIcon
    case 'Positive':
      return PositiveCircleFillIcon
    case 'Negative':
      return NegativeCircleFillIcon
    case 'Warning':
      return WarningTriangleFillIcon
    default:
      return undefined
  }
}
type PromoBannerPropsType = ComponentProps<typeof PromoBanner>
// change to single value
function mapVariantToPromoVariant(
  variant?: PromoBannerType['variant'],
): PromoBannerPropsType['variant'] {
  if (!variant) return undefined

  switch (variant) {
    case 'Neutral':
      return 'neutral'
    case 'Brand':
      return 'brand'
    default:
      return undefined
  }
}
function mapAstroIconToIcon(astro_icon: PromoBannerType['astro_icon']): ElementType | undefined {
  if (!astro_icon) return undefined

  switch (astro_icon) {
    case 'FavoriteIcon':
      return FavoriteIcon
    case 'BuildingStoreIcon':
      return BuildingStoreIcon
    default:
      return undefined
  }
}
export const AstroBanner = ({ content_type_id, id, data }: AstroBannerData) => {
  try {
    return (
      <AstroBannerContainer {...getCMSWrapperProps({ content_type_id, id })}>
        {data.banner_type.content_type_id === 'feedback_banner' && (
          <FeedbackBanner
            variant={mapVariantToVariant(data.banner_type.data.variant)}
            title={data.title}
            actionButton={
              data.button
                ? {
                    variant: data.button.data.variant,
                    content: data.button.data.text,
                  }
                : undefined
            }
            // with new version, we do not need icon property. It'll be set by the variat
            icon={mapVariantToIcon(data.banner_type.data.variant)}
            description={data.description?.elements.map(renderElement)}
            hasEmphasis={data.has_emphasis}
          ></FeedbackBanner>
        )}
        {data.banner_type.content_type_id === 'promo_banner_type' &&
          data.banner_type.data.variant === 'Brand-Refresh' && (
            <RefreshPromoBanner title={data.title} />
          )}
        {data.banner_type.content_type_id === 'promo_banner_type' &&
          data.banner_type.data.variant !== 'Brand-Refresh' && (
            <PromoBanner
              title={data.title}
              description={data.description?.elements.map(renderElement)}
              hasEmphasis={data.has_emphasis}
              variant={mapVariantToPromoVariant(data.banner_type.data.variant)}
              icon={mapAstroIconToIcon(data.banner_type.data.astro_icon)}
            />
          )}
        {data.banner_type.content_type_id === 'promo_asset_banner_type' && (
          <PromoAssetBanner
            title={data.title}
            description={data.description?.elements.map(renderElement)}
            asset={
              data.banner_type.data.image && (
                <Image
                  alt={data.banner_type.data.image.alt || data.title}
                  src={data.banner_type.data.image.src}
                  // I don't believe this should come from CMS. Should we hardcode this here? Is there a minimum or maximum allowed height/width for this banner? Should this be a dropdown option in CMS e.g. image_size: small, medium, large which maps to width/height in code?
                  width={data.banner_type.data.image.width}
                  height={data.banner_type.data.image.height}
                />
              )
            }
            hasEmphasis={data.has_emphasis}
            variant={data.banner_type.data.variant === 'Brand' ? 'brand' : 'neutral'}
            size={data.banner_type.data.size === 'Large' ? 'lg' : 'sm'}
            type={data.banner_type.data.image_alignment === 'Trailing' ? 'trailing' : 'leading'}
          />
        )}
      </AstroBannerContainer>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

// overwrite width: 100% that's on CMS Container. Clean this up when we cut over
// GridLayoutContainer
const AstroBannerContainer = styled(GridLayoutContainer)`
  width: 100%;
  --content-max-width: 1280px;
`

interface RefreshPromoBannerProps {
  title: string
}

const RefreshPromoBanner = ({ title }: RefreshPromoBannerProps) => {
  const { trackCMSElementViewed, trackCMSElementClicked } = useCMSTrackingEvents()
  const searchParams = useSearchParams()
  const params = getTargetCircleQueryParams(searchParams)
  return (
    <ScrollIntoView
      onInView={() => {
        trackCMSElementViewed({
          content: title,
        })
      }}
    >
      <RefreshPromoBannerBase
        padding={{ base: ['lg'], md: ['lg', 'xxl'] }}
        align={{ sm: 'center' }}
        direction={{ base: 'column', sm: 'row' }}
        spacing={{ base: 'md', md: 'xl' }}
      >
        <DealIcon surface="inverse" />
        <Box flexBasis="60%" flexGrow={1}>
          <Headline weight="medium" surface="inverse">
            {title}
          </Headline>
        </Box>
        <TextButton
          concept="accent"
          surface="inverse"
          trailingIcon={ChevronRightIcon}
          forwardedAs="a"
          href={`${routes.LOGIN.url}${params}`}
          isLink
          onClick={() => trackCMSElementClicked({ content: 'Try now' })}
        >
          Try now
        </TextButton>
      </RefreshPromoBannerBase>
    </ScrollIntoView>
  )
}

const RefreshPromoBannerBase = styled(Stack)`
  background: #038262;
  border-radius: 20px;

  & > svg:first-child {
    height: 40px;
    width: 40px;

    path {
      stroke-width: 1px;
    }
  }
`
